import React from 'react';
import type { Language } from '@/lib/i18n';
import { t } from '@/lib/i18n';
import { BookOpen } from 'lucide-react';

interface TheoryStepData {
  type: 'theory';
  title: { fr: string; ru: string };
  content: { fr: string; ru: string };
  keyConceptFr?: string;
  keyConceptRu?: string;
}

interface Props {
  step: TheoryStepData;
  language: Language;
}

export default function TheoryStep({ step, language }: Props) {
  const content = step.content[language];

  // Simple markdown-like rendering for bold and line breaks
  const renderContent = (text: string) => {
    return text.split('\n').map((line, i) => {
      const parts = line.split(/\*\*(.*?)\*\*/g);
      return (
        <p key={`theory-line-${i}`} className="text-sm text-foreground leading-relaxed mb-2">
          {parts.map((part, j) =>
            j % 2 === 1 ? (
              <strong key={`bold-${i}-${j}`} className="font-700 text-primary">
                {part}
              </strong>
            ) : (
              part
            )
          )}
        </p>
      );
    });
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <BookOpen size={20} className="text-primary" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground font-500">{t(language, 'lesson_theory')}</p>
          <h2 className="text-lg font-700 text-foreground">{step.title[language]}</h2>
        </div>
      </div>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-5">
        {renderContent(content)}
      </div>

      {/* Key concept box */}
      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
        <p className="text-xs font-700 text-amber-700 uppercase tracking-wide mb-1">
          {language === 'fr' ? '💡 Concept clé' : '💡 Ключевая концепция'}
        </p>
        <p className="text-sm text-amber-900 font-500">
          {language === 'fr' ? (step.keyConceptFr ??'La comptabilité en partie double garantit que chaque transaction est enregistrée de manière équilibrée.')
            : (step.keyConceptRu ?? 'Двойная бухгалтерия гарантирует, что каждая транзакция записывается сбалансированно.')}
        </p>
      </div>
    </div>
  );
}